import React from 'react'
import { Plus, X, MessageCircle } from 'lucide-react'
import { ShadcnButton } from '../ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Checkbox } from '../ui/checkbox'
import { Label } from '../ui/label'
import { PhoneInputSeparated } from './PhoneInputSeparated'
import { LeadPhone } from '../../types'

interface MultiPhoneInputProps {
  value: LeadPhone[]
  onChange: (phones: LeadPhone[]) => void
  error?: string
  required?: boolean
}

export const MultiPhoneInput: React.FC<MultiPhoneInputProps> = ({
  value = [],
  onChange
}) => {
  const handleAdd = () => {
    onChange([
      ...value,
      {
        ddi: '+55',
        ddd: '',
        phone: '',
        type: 'personal',
        isPrimary: value.length === 0,
        isWhatsApp: true
      }
    ])
  }

  const handleRemove = (index: number) => {
    const newPhones = value.filter((_, i) => i !== index)
    // Se remover o primary, marca o primeiro como primary
    if (newPhones.length > 0 && !newPhones.some(p => p.isPrimary)) {
      newPhones[0].isPrimary = true
    }
    onChange(newPhones)
  }

  const handleDdiChange = (index: number, ddi: string) => {
    const newPhones = [...value]
    newPhones[index] = { ...newPhones[index], ddi }
    onChange(newPhones)
  }

  const handleDddChange = (index: number, ddd: string) => {
    const newPhones = [...value]
    newPhones[index] = { ...newPhones[index], ddd }
    onChange(newPhones)
  }

  const handlePhoneChange = (index: number, phone: string) => {
    const newPhones = [...value]
    newPhones[index] = { ...newPhones[index], phone }
    onChange(newPhones)
  }

  const handleTypeChange = (index: number, type: 'personal' | 'work' | 'other') => {
    const newPhones = [...value]
    newPhones[index] = { ...newPhones[index], type }
    onChange(newPhones)
  }

  const handlePrimaryChange = (index: number) => {
    const newPhones = value.map((p, i) => ({
      ...p,
      isPrimary: i === index
    }))
    onChange(newPhones)
  }

  const handleWhatsAppChange = (index: number) => {
    const newPhones = [...value]
    newPhones[index] = { ...newPhones[index], isWhatsApp: !newPhones[index].isWhatsApp }
    onChange(newPhones)
  }

  // Se não houver nenhum telefone, adiciona um campo vazio
  const phones = value.length === 0
    ? [{
        ddi: '+55',
        ddd: '',
        phone: '',
        type: 'personal' as const,
        isPrimary: true,
        isWhatsApp: true
      }]
    : value

  return (
    <div className="space-y-3">
      {phones.map((phoneObj, index) => (
        <div key={index} className="border border-border rounded-lg p-3 space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <PhoneInputSeparated
                ddi={phoneObj.ddi || '+55'}
                ddd={phoneObj.ddd || ''}
                phone={phoneObj.phone || ''}
                onDdiChange={(val) => handleDdiChange(index, val)}
                onDddChange={(val) => handleDddChange(index, val)}
                onPhoneChange={(val) => handlePhoneChange(index, val)}
              />
            </div>
            {phones.length > 1 && (
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                title="Remover telefone"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          <div className="flex gap-2 flex-wrap items-center">
            <Select
              value={phoneObj.type}
              onValueChange={(value) => handleTypeChange(index, value as 'personal' | 'work' | 'other')}
            >
              <SelectTrigger className="h-8 text-xs w-auto min-w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">Pessoal</SelectItem>
                <SelectItem value="work">Trabalho</SelectItem>
                <SelectItem value="other">Outro</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center space-x-2">
              <Checkbox
                id={`phone-primary-${index}`}
                checked={phoneObj.isPrimary}
                onCheckedChange={() => handlePrimaryChange(index)}
              />
              <Label
                htmlFor={`phone-primary-${index}`}
                className="text-xs font-normal cursor-pointer"
              >
                Principal
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id={`phone-whatsapp-${index}`}
                checked={phoneObj.isWhatsApp}
                onCheckedChange={() => handleWhatsAppChange(index)}
              />
              <Label
                htmlFor={`phone-whatsapp-${index}`}
                className="text-xs font-normal cursor-pointer flex items-center gap-1"
              >
                <MessageCircle className="h-3 w-3 text-green-600" />
                WhatsApp
              </Label>
            </div>
          </div>
        </div>
      ))}

      <ShadcnButton
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAdd}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Adicionar Telefone
      </ShadcnButton>
    </div>
  )
}

